(() => {
  /* ==========================================================
     VARIANT DATA
     Model number + dynamic product title
     ========================================================== */

  const initVariantData = (pdp) => {
    const sectionId =
      pdp.dataset.section;

    if (!sectionId) return;

    const dataElement =
      document.getElementById(
        `TSVariantModels-${sectionId}`
      );

    if (!dataElement) return;

    let variants = [];

    try {
      variants = JSON.parse(
        dataElement.textContent ||
        '[]'
      );
    } catch (error) {
      console.warn(
        'TS PDP: Unable to read variant data.',
        error
      );

      return;
    }

    const variantData =
      new Map(
        variants.map(
          (variant) => [
            String(variant.id),
            {
              mpn: String(
                variant.mpn || ''
              ).trim(),

              size: String(
                variant.size || ''
              ).trim()
            }
          ]
        )
      );


    /* ----------------------------------------------------------
       MODEL NUMBER
       ---------------------------------------------------------- */

    const modelRow =
      pdp.querySelector(
        '[data-ts-model-number]'
      );

    const modelValue =
      modelRow?.querySelector(
        '[data-ts-model-value]'
      );


    const updateModelNumber = (
      variantId
    ) => {
      if (
        !modelRow ||
        !modelValue
      ) {
        return;
      }

      const variant =
        variantData.get(
          String(variantId)
        );

      const mpn =
        variant?.mpn || '';

      if (!mpn) {
        modelValue.textContent =
          '';

        modelRow.hidden =
          true;

        return;
      }

      modelValue.textContent =
        mpn;

      modelRow.hidden =
        false;
    };


    /* ----------------------------------------------------------
       PRODUCT TITLE + SIZE
       ---------------------------------------------------------- */

    const productTitle =
      pdp.querySelector(
        '[data-ts-product-title]'
      );

    const baseTitle =
      productTitle
        ?.dataset
        .tsBaseTitle
        ?.trim() || '';


    const updateProductTitle = (
      variantId
    ) => {
      if (
        !productTitle ||
        !baseTitle
      ) {
        return;
      }

      const variant =
        variantData.get(
          String(variantId)
        );

      const size =
        variant?.size || '';

      /*
        Product without a Size option:
        keep the normal product title.
      */
      if (!size) {
        productTitle.textContent =
          baseTitle;

        return;
      }

      productTitle.textContent =
        `${baseTitle} - ${size}`;
    };


    /* ----------------------------------------------------------
       UPDATE ALL VARIANT-BASED PDP DATA
       ---------------------------------------------------------- */

    const updateVariantData = (
      variantId
    ) => {
      if (!variantId) return;

      /*
        Ignore variants that don't belong
        to this product.
      */
      if (
        !variantData.has(
          String(variantId)
        )
      ) {
        return;
      }

      updateModelNumber(
        variantId
      );

      updateProductTitle(
        variantId
      );
    };


    /* ----------------------------------------------------------
       INITIAL VARIANT
       ---------------------------------------------------------- */

    const initialVariantInput =
      pdp.querySelector(
        'form[action*="/cart/add"] input[name="id"]'
      );

    if (
      initialVariantInput?.value
    ) {
      updateVariantData(
        initialVariantInput.value
      );
    }


    /* ----------------------------------------------------------
       DAWN VARIANT CHANGE
       ---------------------------------------------------------- */

    if (
      typeof subscribe ===
        'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => {
          const data =
            event?.data;

          if (!data) return;

          /*
            Main Product events normally include
            the section ID. Ignore another product
            section if one exists on the page.
          */
          if (
            data.sectionId &&
            String(
              data.sectionId
            ) !==
            String(
              sectionId
            )
          ) {
            return;
          }

          const variantId =
            data.variant?.id;

          if (variantId) {
            updateVariantData(
              variantId
            );
          }
        }
      );
    }


    /* ----------------------------------------------------------
       FALLBACK

       Supports customized variant pickers that do
       not publish Dawn's standard variantChange event.
       ---------------------------------------------------------- */

    pdp.addEventListener(
      'change',
      (event) => {
        if (
          !event.target.closest(
            'variant-selects'
          )
        ) {
          return;
        }

        /*
          Give Dawn a moment to update
          the hidden selected-variant input.
        */
        window.setTimeout(
          () => {
            const variantInput =
              pdp.querySelector(
                'form[action*="/cart/add"] input[name="id"]'
              );

            if (
              variantInput?.value
            ) {
              updateVariantData(
                variantInput.value
              );
            }
          },
          100
        );
      }
    );
  };


  /* ==========================================================
     DISCOUNT BADGE
     ========================================================== */

  const initDiscountBadge = (
    pdp
  ) => {
    const sectionId =
      pdp.dataset.section;

    if (!sectionId) return;

    const priceLine =
      pdp.querySelector(
        `#price-${CSS.escape(
          sectionId
        )}`
      );

    if (!priceLine) return;

    let queued =
      false;


    const placeDiscountBadge =
      () => {
        queued = false;

        const currency =
          priceLine.querySelector(
            '.ts-pdp__currency'
          );

        const priceRoot =
          priceLine.querySelector(
            '.ts-pdp__price'
          );

        if (
          !currency ||
          !priceRoot
        ) {
          return;
        }

        const discountBadge =
          Array.from(
            priceRoot
              .querySelectorAll(
                '*'
              )
          ).find(
            (element) => {
              if (
                element.children
                  .length >
                0
              ) {
                return false;
              }

              const text =
                (
                  element
                    .textContent ||
                  ''
                ).trim();

              return (
                /^-\d+(?:\.\d+)?%$/.test(
                  text
                ) ||
                /^\d+(?:\.\d+)?%\s*off$/i.test(
                  text
                )
              );
            }
          );

        if (
          !discountBadge
        ) {
          return;
        }

        discountBadge
          .classList
          .add(
            'ts-pdp__discount-percent'
          );

        if (
          currency
            .nextElementSibling !==
          discountBadge
        ) {
          currency
            .insertAdjacentElement(
              'afterend',
              discountBadge
            );
        }
      };


    const queuePlacement =
      () => {
        if (queued) return;

        queued = true;

        window
          .requestAnimationFrame(
            placeDiscountBadge
          );
      };


    placeDiscountBadge();


    /*
      Only watches the price line.

      Does NOT touch:
      - Shopify dynamic checkout
      - Smile.io
      - Essential Upsell
      - app-block ordering
    */
    const observer =
      new MutationObserver(
        queuePlacement
      );

    observer.observe(
      priceLine,
      {
        childList: true,
        subtree: true
      }
    );
  };


  /* ==========================================================
     INITIALIZATION
     ========================================================== */

  const initPdp = (
    pdp
  ) => {
    if (
      !pdp ||
      pdp.dataset
        .tsMainProductInitialized ===
        'true'
    ) {
      return;
    }

    pdp.dataset
      .tsMainProductInitialized =
      'true';

    initVariantData(
      pdp
    );

    initDiscountBadge(
      pdp
    );
  };


  const initAll = (
    root = document
  ) => {
    if (
      root.matches?.(
        '.ts-pdp'
      )
    ) {
      initPdp(
        root
      );
    }

    root
      .querySelectorAll?.(
        '.ts-pdp'
      )
      .forEach(
        initPdp
      );
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document
      .addEventListener(
        'DOMContentLoaded',
        () => {
          initAll(
            document
          );
        },
        {
          once: true
        }
      );
  }

  else {
    initAll(
      document
    );
  }


  /*
    Shopify Theme Editor support.
  */
  document
    .addEventListener(
      'shopify:section:load',
      (event) => {
        initAll(
          event.target
        );
      }
    );
})();